import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Hammer, Check, Loader2, ClipboardList, Rocket, Download, X, AlertCircle, ArrowRight, ChevronDown, CheckCircle2 } from 'lucide-react';
import { api } from '../../lib/api';
import { CreditBanner, isCreditError } from './CreditBanner';
import { Button } from '../ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { SectionCard, Field, CopyButton, Skeleton } from './ui';
import { ServedModelBadge } from './ServedModelBadge';
import { RegenerateWithModel } from './RegenerateWithModel';

// Scaffolding strings for the exported Lovable prompt. The user can pick the export
// language independently of the project / UI language. Generated step bodies keep the
// language they were produced in; only the wrapper/instructions follow this map.
const EXPORT_STRINGS = {
  en: {
    fallbackTitle: 'My App', brief: 'Project brief', architecture: 'Architecture',
    naming: 'Naming conventions', subsystems: 'Subsystems', context: 'Context & conventions',
    stepByStep: 'Build this app step by step, in order', phase: 'Phase', doneWhen: 'Done when',
    footer: 'Build incrementally. Implement one step at a time and verify it works before moving to the next.',
  },
  es: {
    fallbackTitle: 'Mi App', brief: 'Resumen del proyecto', architecture: 'Arquitectura',
    naming: 'Convenciones de nombres', subsystems: 'Subsistemas', context: 'Contexto y convenciones',
    stepByStep: 'Crea esta app paso a paso, en orden', phase: 'Fase', doneWhen: 'Listo cuando',
    footer: 'Construye de forma incremental. Implementa un paso a la vez y verifica que funcione antes de pasar al siguiente.',
  },
  fa: {
    fallbackTitle: 'اپلیکیشن من', brief: 'خلاصه پروژه', architecture: 'معماری',
    naming: 'قراردادهای نام‌گذاری', subsystems: 'زیرسیستم‌ها', context: 'زمینه و قراردادها',
    stepByStep: 'این اپلیکیشن را گام‌به‌گام و به‌ترتیب بساز', phase: 'فاز', doneWhen: 'انجام‌شده وقتی',
    footer: 'به‌صورت تدریجی بساز. هر بار یک گام را پیاده‌سازی کن و پیش از رفتن به گام بعد از کارکرد آن مطمئن شو.',
  },
};
const exportStrings = (lang) => EXPORT_STRINGS[lang] || EXPORT_STRINGS.en;

function TypeBadge({ type }) {
  const map = {
    action: 'bg-muted text-foreground border-border',
    build_prompt: 'bg-blue-50 text-blue-700 border-blue-200',
    ops_prompt: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return <span className={`text-xs font-medium px-2 py-0.5 rounded border ${map[type] || ''}`}>{type}</span>;
}

function buildCopyAll(pack) {
  if (!pack) return '';
  const lines = [pack.mega_prompt, ''];
  (pack.phases || []).forEach((ph) => {
    (ph.steps || []).forEach((s) => {
      lines.push(`--- Step ${s.n}: ${s.title} ---`);
      lines.push(s.prompt_body || s.instruction || '');
      lines.push('');
    });
  });
  return lines.join('\n');
}

function lovableBriefSection(a, s) {
  const lines = [`\n## ${s.brief}`];
  if (a.architecture) lines.push(`**${s.architecture}:** ${a.architecture}`);
  if (a.naming_conventions) lines.push(`**${s.naming}:** ${a.naming_conventions}`);
  if (Array.isArray(a.subsystems) && a.subsystems.length) {
    lines.push(`**${s.subsystems}:**`);
    a.subsystems.forEach((sub) => lines.push(`- ${sub.title}${sub.description ? `: ${sub.description}` : ''}`));
  }
  return lines;
}

function lovableStepsSection(phases, s) {
  const lines = [`\n## ${s.stepByStep}`];
  let idx = 1;
  (phases || []).forEach((ph) => {
    lines.push(`\n### ${s.phase} ${ph.phase}: ${ph.title}`);
    (ph.steps || []).forEach((step) => {
      lines.push(`\n**${idx}. ${step.title}**`);
      const stepBody = step.prompt_body || step.instruction;
      if (stepBody) lines.push(stepBody);
      if (step.success_when) lines.push(`_${s.doneWhen}: ${step.success_when}_`);
      idx += 1;
    });
  });
  return lines;
}

function buildLovablePrompt(pack, lang) {
  if (!pack) return '';
  const s = exportStrings(lang);
  const a = pack.anchor_spec || {};
  const lines = [`# ${a.product_name || s.fallbackTitle}`];
  if (a.one_liner) lines.push(`\n${a.one_liner}`);
  lines.push(...lovableBriefSection(a, s));
  if (pack.mega_prompt) lines.push(`\n## ${s.context}`, pack.mega_prompt);
  lines.push(...lovableStepsSection(pack.phases, s));
  lines.push('\n---', s.footer);
  return lines.join('\n');
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function slugify(s) {
  return (s || 'app').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'app';
}

function LovableExportModal({ pack, accountLanguage, onClose }) {
  const { t } = useTranslation();
  const acct = ['en', 'fa', 'es'].includes(accountLanguage) ? accountLanguage : 'en';
  const options = acct === 'en' ? ['en'] : ['en', acct];
  const [lang, setLang] = useState(acct);
  const prompt = buildLovablePrompt(pack, lang);
  const name = pack?.anchor_spec?.product_name;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="lovable-export-modal">
      <div className="bg-card rounded-lg w-full max-w-2xl flex flex-col gap-4 max-h-[85vh] p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-lg font-semibold text-foreground flex items-center gap-2"><Rocket className="h-5 w-5 text-primary" /> {t('build.exportTitle')}</h3>
            <p className="text-sm text-muted-foreground"><T k="build.exportSubtitle" /></p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="close-lovable-export"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-foreground whitespace-nowrap" htmlFor="export-language"><T k="build.exportLanguage" /></label>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger id="export-language" className="w-44" data-testid="export-language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map((code) => (
                <SelectItem key={code} value={code} data-testid={`export-language-${code}`}>{t(`language.${code}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <pre dir={lang === 'fa' ? 'rtl' : 'ltr'} className="font-data text-xs bg-muted border border-border rounded-md p-4 overflow-auto whitespace-pre-wrap text-foreground flex-1" data-testid="lovable-export-content">{prompt}</pre>
        <div className="flex flex-wrap gap-3 justify-end">
          <CopyButton text={prompt} label={<T k="build.exportCopy" />} testId="copy-lovable-prompt" />
          <Button variant="outline" onClick={() => downloadText(`${slugify(name)}-lovable-prompt.md`, prompt)} data-testid="download-lovable-prompt">
            <Download className="h-4 w-4 me-2" /> {t('build.exportDownload')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StepCard({ s, error, onRetry, loading, footer }) {
  const { t } = useTranslation();
  const body = s.prompt_body || s.instruction;
  return (
    <div className="border border-border bg-card rounded-xl shadow-sm" data-testid={`build-step-${s.n}`}>
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
        <div className="flex items-center gap-3">
          <span className="font-data text-sm text-muted-foreground">{String(s.n).padStart(2, '0')}</span>
          <span className="font-semibold text-foreground">{s.title}</span>
          <TypeBadge type={s.type} />
        </div>
        {body && <CopyButton text={body} testId={`copy-prompt-step-${s.n}`} />}
      </div>
      <div className="p-4 flex flex-col gap-4">
        {s.success_when && <Field label={<T k="build.stepSuccessWhen" />}>{s.success_when}</Field>}
        {Array.isArray(s.verify) && s.verify.length > 0 && (
          <Field label={<T k="build.stepVerify" />}>
            <ul className="space-y-1">{s.verify.map((v) => <li key={v} className="flex gap-2 text-sm"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" />{v}</li>)}</ul>
          </Field>
        )}
        {body && (
          <pre className="font-data text-xs bg-muted border border-border rounded-md p-4 overflow-x-auto whitespace-pre-wrap text-foreground" data-testid={`build-step-body-${s.n}`}>{body}</pre>
        )}
        {!body && loading && (
          <div className="flex flex-col gap-2.5" data-testid={`build-step-skeleton-${s.n}`}>
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
            <Skeleton className="h-28 w-full" />
          </div>
        )}
        {!body && !loading && error && (
          <div className="bg-destructive-soft border border-destructive-border rounded-md p-4 flex items-start gap-3" data-testid={`build-step-error-${s.n}`}>
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-destructive font-medium"><T k="build.stepFailed" /></p>
              <p className="text-xs text-destructive font-data break-words">{error}</p>
            </div>
            {onRetry && (
              <Button size="sm" variant="outline" onClick={() => onRetry(s.n)} data-testid={`retry-step-${s.n}`}><T k="build.retry" /></Button>
            )}
          </div>
        )}
        {!body && !loading && !error && (
          <p className="text-sm text-muted-foreground" data-testid={`build-step-empty-${s.n}`}><T k="build.stepNotGenerated" /></p>
        )}
        {s.tip && <p className="text-xs text-muted-foreground">💡 {s.tip}</p>}
        {footer && <div className="pt-1">{footer}</div>}
      </div>
    </div>
  );
}

function CollapsedRow({ s, done, status, onClick }) {
  const num = String(s.n).padStart(2, '0');
  let badge;
  if (done) badge = <CheckCircle2 className="h-5 w-5 text-success" />;
  else if (status === 'error') badge = <AlertCircle className="h-5 w-5 text-destructive" />;
  else if (status === 'active') badge = <Loader2 className="h-5 w-5 text-foreground animate-spin" />;
  else if (status === 'pending') badge = <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />;
  else badge = <span className="font-data text-sm text-muted-foreground w-5 text-center">{num}</span>;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`build-step-row-${s.n}`}
      className="w-full flex items-center gap-3 p-4 border border-border rounded-xl bg-muted hover:border-border transition-colors text-start"
    >
      <span className="shrink-0 flex items-center justify-center">{badge}</span>
      <span className="flex-1 min-w-0 font-medium text-foreground truncate">{s.title}</span>
      <TypeBadge type={s.type} />
      <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}

function AnchorCard({ a, megaPrompt }) {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={a.product_name || t('build.anchorFallback')}
      testId="anchor-spec-card"
      action={megaPrompt && <CopyButton text={megaPrompt} label={<T k="build.copyMega" />} testId="copy-mega-prompt" />}
    >
      <p className="text-foreground">{a.one_liner}</p>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={<T k="build.fieldArchitecture" />}>{a.architecture}</Field>
        <Field label={<T k="build.fieldNaming" />}>{a.naming_conventions}</Field>
      </div>
      {Array.isArray(a.subsystems) && (
        <Field label={t('build.fieldSubsystems', { count: a.subsystems.length })}>
          <div className="flex flex-wrap gap-2">
            {a.subsystems.map((s) => (
              <span key={s.id} className="text-xs font-data px-2 py-1 bg-muted rounded border border-border">{s.title}</span>
            ))}
          </div>
        </Field>
      )}
    </SectionCard>
  );
}

// Sequential reveal: only the active step is expanded; others are collapsed rows.
// "Mark complete & continue" collapses the current step and slides the next one open.
function SequentialPackView({ pack, stepErrors, onRetryStep, tasks }) {
  const { t } = useTranslation();
  const flat = useMemo(
    () => (pack.phases || [])
      .flatMap((ph) => (ph.steps || []).map((s) => ({ ...s, phaseTitle: ph.title, phase: ph.phase })))
      .sort((a, b) => (a.n || 0) - (b.n || 0)),
    [pack],
  );
  const total = flat.length;
  const [activeN, setActiveN] = useState(null);
  const [completed, setCompleted] = useState(() => new Set());

  useEffect(() => {
    if (activeN == null && flat.length) setActiveN(flat[0].n);
  }, [flat, activeN]);

  const generating = !!tasks;
  const statusOf = (n) => tasks?.find((tk) => tk.n === n)?.status;
  const activeIdx = flat.findIndex((s) => s.n === activeN);
  const genDone = tasks ? tasks.filter((tk) => tk.status === 'done').length : 0;
  const completePct = total ? Math.round((completed.size / total) * 100) : 0;
  const genPct = total ? Math.round((genDone / total) * 100) : 0;

  const markComplete = () => {
    setCompleted((prev) => { const nx = new Set(prev); nx.add(activeN); return nx; });
    const next = flat[activeIdx + 1];
    if (next) setActiveN(next.n);
  };

  return (
    <>
      <AnchorCard a={pack.anchor_spec || {}} megaPrompt={pack.mega_prompt} />

      <div className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm border border-border rounded-xl p-4" data-testid="build-step-progress">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-medium text-foreground" data-testid="build-step-progress-label">
            {generating
              ? t('build.generatingProgress', { done: genDone, total })
              : t('build.stepProgress', { current: Math.min(activeIdx + 1, total) || 0, total, percent: completePct })}
          </span>
          {!generating && total > 0 && completed.size >= total && (
            <span className="text-success font-medium" data-testid="build-all-complete"><T k="build.allComplete" /></span>
          )}
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
            style={{ width: `${generating ? genPct : completePct}%` }}
            data-testid="build-progress-fill"
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {flat.map((s) => {
          const isActive = s.n === activeN;
          const isDone = completed.has(s.n);
          const body = s.prompt_body || s.instruction;
          const st = statusOf(s.n);
          const loading = generating && !body && st !== 'error';
          if (!isActive) {
            return <CollapsedRow key={s.n} s={s} done={isDone} status={st} onClick={() => setActiveN(s.n)} />;
          }
          return (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="overflow-hidden"
            >
              <StepCard
                s={s}
                error={stepErrors?.[s.n]}
                onRetry={onRetryStep}
                loading={loading}
                footer={body ? (
                  isDone
                    ? (
                      <div className="flex items-center gap-2 text-sm text-success font-medium" data-testid={`step-completed-${s.n}`}>
                        <CheckCircle2 className="h-4 w-4" /> {t('build.stepDone')}
                      </div>
                    )
                    : (
                      <Button onClick={markComplete} data-testid={`mark-complete-${s.n}`}>
                        {t('build.markComplete')} <ArrowRight className="h-4 w-4 ms-2" />
                      </Button>
                    )
                ) : null}
              />
            </motion.div>
          );
        })}
      </div>
    </>
  );
}

function mergeFilledStep(pack, filled) {
  if (!pack || !filled) return pack;
  return {
    ...pack,
    phases: (pack.phases || []).map((ph) => ({
      ...ph,
      steps: (ph.steps || []).map((s) => (s.n === filled.n ? { ...s, ...filled } : s)),
    })),
  };
}

const shortErr = (m) => (m && m.length > 160 ? `${m.slice(0, 160)}…` : m);

async function runBuildPackSteps({ projectId, toFill, abortRef, setTasks, setStepErrors, setPack, t, opts = {} }) {
  const failed = [];
  for (const n of toFill) {
    if (abortRef.current) return { aborted: true, failed };
    setTasks((prev) => prev.map((task) => (task.n === n ? { ...task, status: 'active' } : task)));
    try {
      const res = await api.buildPackStep(projectId, n, opts);
      if (res?.filled_step) setPack((prev) => mergeFilledStep(prev, res.filled_step));
      setTasks((prev) => prev.map((task) => (task.n === n ? { ...task, status: 'done' } : task)));
      setStepErrors((prev) => {
        if (!(n in prev)) return prev;
        const next = { ...prev }; delete next[n]; return next;
      });
    } catch (e) {
      // Out of credits: every later step would 402 too — stop the loop, mark this step and
      // every remaining one as waiting, and let the credit banner's resume re-run them
      // (idempotency: filled steps are never re-charged).
      if (isCreditError(e)) {
        const rest = toFill.slice(toFill.indexOf(n));
        rest.forEach((k) => failed.push(k));
        const msg = t('credits.waitRow');
        setStepErrors((prev) => ({ ...prev, ...Object.fromEntries(rest.map((k) => [k, msg])) }));
        setTasks((prev) => prev.map((task) => (rest.includes(task.n) ? { ...task, status: 'error' } : task)));
        return { aborted: false, failed, creditBlocked: true };
      }
      // Do NOT abort the loop — log the failure on this step and continue to the next n.
      failed.push(n);
      const msg = e?.message || 'Generation failed';
      setStepErrors((prev) => ({ ...prev, [n]: msg }));
      setTasks((prev) => prev.map((task) => (task.n === n ? { ...task, status: 'error' } : task)));
      toast.error(t('build.stepError', { number: n, message: shortErr(msg) }));
    }
  }
  return { aborted: false, failed, creditBlocked: false };
}

export default function BuildPackStage({ projectId, hasIdea, onChanged, servedModels }) {
  const { t, i18n } = useTranslation();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState(null);
  const [stepErrors, setStepErrors] = useState({});
  const [creditBlocked, setCreditBlocked] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const abortRef = useRef(false);
  // Pass-1 prompt-generation language: English or the account language (default). Sent to
  // generate-build-pack; the pack persists it and every step reuses it (one consistent language).
  const accountCode = ['en', 'es', 'fa'].includes(i18n.language) ? i18n.language : 'en';
  const [promptLang, setPromptLang] = useState(accountCode);
  const [planning, setPlanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.out('get_build_pack', { project_id: projectId });
      setPack(r.build_pack);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => {
    abortRef.current = false;
    load();
    return () => { abortRef.current = true; };
  }, [load]);

  const generate = async () => {
    try {
      setStepErrors({});
      setPlanning(true);
      const plan = await api.buildPackPlan(projectId, undefined, promptLang);
      setPlanning(false);
      const toFill = plan.steps_to_fill || [];
      setPack(plan.build_pack);
      setTasks(toFill.map((n) => ({ n, status: 'pending' })));
      const { aborted, failed, creditBlocked: cb } = await runBuildPackSteps({ projectId, toFill, abortRef, setTasks, setStepErrors, setPack, t });
      setCreditBlocked(!!cb);
      setTasks(null);
      if (aborted) return;
      await load();
      onChanged?.();
      if (failed.length === 0) {
        toast.success(t('build.ready'));
      } else {
        toast.warning(t('build.partial', { done: toFill.length - failed.length, total: toFill.length, failed: failed.length }));
      }
    } catch (e) {
      if (isCreditError(e)) setCreditBlocked(true);
      else toast.error(e.message);
      setTasks(null);
      setPlanning(false);
    }
  };

  const retryStep = async (n) => {
    setStepErrors((prev) => {
      const next = { ...prev }; delete next[n]; return next;
    });
    try {
      await api.buildPackStep(projectId, n);
      await load();
      onChanged?.();
      toast.success(t('build.stepRetried', { number: n }));
    } catch (e) {
      const msg = e?.message || 'Generation failed';
      setStepErrors((prev) => ({ ...prev, [n]: msg }));
      toast.error(t('build.stepError', { number: n, message: shortErr(msg) }));
    }
  };

  const retryAllFailed = async () => {
    const failedNs = Object.keys(stepErrors).map(Number).sort((a, b) => a - b);
    if (!failedNs.length) return;
    setTasks(failedNs.map((n) => ({ n, status: 'pending' })));
    const { aborted, failed, creditBlocked: cb } = await runBuildPackSteps({ projectId, toFill: failedNs, abortRef, setTasks, setStepErrors, setPack, t });
    setCreditBlocked(!!cb);
    setTasks(null);
    if (aborted) return;
    await load();
    onChanged?.();
    if (failed.length === 0) toast.success(t('build.ready'));
    else toast.warning(t('build.partial', { done: failedNs.length - failed.length, total: failedNs.length, failed: failed.length }));
  };

  const failedCount = Object.keys(stepErrors).length;

  // Re-run every fillable step with an EXPLICIT model override (regenerate:true skips the idempotency
  // short-circuit so filled steps re-generate). A resolves the model, B bills the served one.
  const regenerateWith = async (modelId) => {
    const toFill = (pack?.phases || [])
      .flatMap((ph) => (ph.steps || []).filter((s) => s.type === 'build_prompt' || s.type === 'ops_prompt').map((s) => s.n));
    if (!toFill.length) return;
    setStepErrors({});
    setTasks(toFill.map((n) => ({ n, status: 'pending' })));
    const { aborted, failed, creditBlocked: cb } = await runBuildPackSteps({
      projectId, toFill, abortRef, setTasks, setStepErrors, setPack, t,
      opts: { model_id: modelId, regenerate: true },
    });
    setCreditBlocked(!!cb);
    setTasks(null);
    if (aborted) return;
    await load();
    onChanged?.();
    if (failed.length === 0) toast.success(t('build.ready'));
    else toast.warning(t('build.partial', { done: toFill.length - failed.length, total: toFill.length, failed: failed.length }));
  };

  const filledOnce = pack && (pack.phases || []).some((ph) => (ph.steps || []).some((s) => s.prompt_body));

  let body;
  if (loading) {
    body = <SectionCard testId="build-loading"><Skeleton className="h-40 w-full" /></SectionCard>;
  } else if (pack) {
    body = <SequentialPackView pack={pack} stepErrors={stepErrors} onRetryStep={retryStep} tasks={tasks} />;
  } else {
    body = <div className="text-muted-foreground text-sm px-2" data-testid="build-empty"><T k="build.empty" /></div>;
  }

  return (
    <div className="flex flex-col gap-3">
      {showExport && <LovableExportModal pack={pack} accountLanguage={i18n.language} onClose={() => setShowExport(false)} />}

      <SectionCard
        title={t('build.title')}
        testId="build-action-card"
        action={
          <div className="flex flex-wrap gap-3 justify-end">
            <Button onClick={generate} disabled={!hasIdea || !!tasks || planning || creditBlocked} data-testid="generate-build-pack-button">
              <Hammer className="h-4 w-4 me-2" /> {filledOnce ? t('build.rebuild') : t('build.generate')}
            </Button>
            {failedCount > 0 && !tasks && !creditBlocked && (
              <Button variant="outline" onClick={retryAllFailed} data-testid="retry-all-failed-button">
                <AlertCircle className="h-4 w-4 me-2" /> {t('build.retryAll', { count: failedCount })}
              </Button>
            )}
            {filledOnce && (
              <>
                <Button onClick={() => setShowExport(true)} data-testid="export-lovable-button">
                  <Rocket className="h-4 w-4 me-2" /> {t('build.export')}
                </Button>
                <CopyButton text={buildCopyAll(pack)} label={<T k="build.copyAll" />} testId="copy-all-button" />
              </>
            )}
          </div>
        }
      >
        {!hasIdea && <p className="text-sm text-muted-foreground"><T k="build.needIdea" /></p>}
        <p className="text-sm text-muted-foreground flex items-center gap-2"><ClipboardList className="h-4 w-4" /> {t('build.help')}</p>
        {hasIdea && accountCode !== 'en' && (
          <div className="flex items-center gap-3 mt-1" data-testid="build-prompt-language">
            <label className="text-sm font-medium text-foreground whitespace-nowrap" htmlFor="build-prompt-lang-select"><T k="build.promptLanguage" /></label>
            <Select value={promptLang} onValueChange={setPromptLang} disabled={!!tasks || planning}>
              <SelectTrigger id="build-prompt-lang-select" className="w-44" data-testid="build-prompt-lang-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en" data-testid="build-prompt-lang-en"><T k="language.en" /></SelectItem>
                <SelectItem value={accountCode} data-testid={`build-prompt-lang-${accountCode}`}>{t(`language.${accountCode}`)}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {filledOnce && (
          <div className="mt-1 flex flex-col gap-2">
            <ServedModelBadge model={servedModels?.['generate-build-pack-step'] || servedModels?.['generate-build-pack']} testId="build-served-model" />
            <RegenerateWithModel busy={!!tasks} onRegenerate={regenerateWith} testId="build-regenerate" />
          </div>
        )}
      </SectionCard>

      {creditBlocked && !tasks && (
        <SectionCard testId="build-credit-card">
          <CreditBanner testId="build-credit-banner" onResume={retryAllFailed} />
        </SectionCard>
      )}

      {planning && (
        <SectionCard testId="build-planning">
          <div className="flex items-center gap-3 text-muted-foreground" data-testid="build-planning-indicator">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm font-medium"><T k="build.progressTitle" /></span>
          </div>
        </SectionCard>
      )}

      {body}
    </div>
  );
}
